import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  HttpResponse,
  JobCountsWithLocationMetricsTableRow,
  JobLocationMetricsTimeseriesResponse,
  JobLocationStateCount,
  StatewiseJobCountPoint,
} from "@/types";

const TWELVE_HOURS_IN_MS = 1000 * 60 * 60 * 12;

const QUERY_KEY = ["metrics/scraped-jobs/location-metrics-by-state/timeseries"];

const queryFn = async () => {
  const path = "metrics/scraped-jobs/location-metrics-by-state/timeseries";
  const res = await guacHttpClient.get<
    JobLocationMetricsTimeseriesResponse,
    HttpResponse<JobLocationMetricsTimeseriesResponse>
  >(path);
  return res.data;
};

// Function overloads
export function useGetStatewiseScrapedJobCountTimeseries(
  state: string,
): UseQueryResult<StatewiseJobCountPoint[]>;
export function useGetStatewiseScrapedJobCountTimeseries(
  state: null,
): UseQueryResult<JobCountsWithLocationMetricsTableRow[]>;
export function useGetStatewiseScrapedJobCountTimeseries(
  state: string | null,
): UseQueryResult<
  StatewiseJobCountPoint[] | JobCountsWithLocationMetricsTableRow[]
> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn,
    staleTime: TWELVE_HOURS_IN_MS,
    select: (raw: JobLocationMetricsTimeseriesResponse) => {
      // Latest entry in the timeseries
      if (state === null) {
        const latest = raw[raw.length - 1];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!latest) return [];
        return Object.entries(latest.statesWithCounts).map(([s, counts]) => ({
          state: s,
          preciseLocationJobCount: counts.precise,
          standardLocationJobCount: counts.standard,
          total: counts.total,
        }));
      }

      // Timeseries - runs a select on cached data, flatten to process per state
      return [...raw].map((entry) => {
        const counts = entry.statesWithCounts as Record<
          string,
          JobLocationStateCount | undefined
        >;
        const statewiseCount = counts[state];
        return {
          date: new Date(entry.date).toLocaleDateString("en-CA"), // "en-CA" gives YYYY-MM-DD in local time
          total: statewiseCount?.total ?? 0,
          precise: statewiseCount?.precise ?? 0,
          standard: statewiseCount?.standard ?? 0,
        };
      });
    },
  });
}
